import React, {createContext, useContext, useMemo, useReducer} from 'react';
import {ActionType} from 'typesafe-actions';
import {DragAndDropActionTypes} from './DragAndDropActionTypes';

export type DragAndDropAction = ActionType<typeof import('./DragAndDropActionTypes')>;

export type DragAndDropState = {
  name: string;
  data: null | object;
  children?: any;
};

export type ContextState = {
  state: DragAndDropState;
  dispatch: React.Dispatch<DragAndDropAction>;
};

const initialState: DragAndDropState = {
  name: '',
  data: null,
};

export const DragAndDropContext = createContext<ContextState>({} as ContextState);

const dragAndDropReducer = (
  state: DragAndDropState,
  action: DragAndDropAction
): DragAndDropState => {
  // Don't return a new object, drag and drop doesn't need to cause a re-render.
  switch (action.type) {
    case DragAndDropActionTypes.SET_DRAG_DATA: {
      state.data = action.payload.data;
      state.name = action.payload.name;
      return state;
    }
    case DragAndDropActionTypes.CLEAR_DRAG_DATA: {
      state.data = null;
      state.name = '';
      return state;
    }
    default:
      return state;
  }
};

export const DragAndDropProvider = (props: Pick<DragAndDropState, 'children'>) => {
  const {children} = props;
  const [state, dispatch] = useReducer(dragAndDropReducer, {
    ...initialState,
  });

  const context = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return <DragAndDropContext.Provider value={context}>{children}</DragAndDropContext.Provider>;
};

export const useDragAndDropState = () => {
  const context = useContext(DragAndDropContext);
  if (context === undefined) {
    throw new Error('useDragAndDropState must be used within a DragAndDropProvider');
  }
  return context;
};
